/**
 * API Chapka - Assurance voyage Cap Explorer
 * 
 * Ce module gère les appels aux endpoints de l'assurance Chapka :
 * - QUOTE : Obtenir le prix de l'assurance
 * - NOTIFY : Finaliser la souscription après paiement
 */

const CHAPKA_API_BASE = 'https://www.ouicsport.fr/api/chapka';

// Types d'offres éligibles à l'assurance
const ELIGIBLE_TYPES = ['Stage', 'Séjour', 'stage/séjour', 'Stage à la carte', 'Stage/Séjour'];

/**
 * Vérifie si un type d'offre est éligible à l'assurance Chapka
 * @param {string} offerType - Type de l'offre
 * @returns {boolean} - True si éligible
 */
export function isInsuranceEligible(offerType) {
  if (!offerType) return false;
  return ELIGIBLE_TYPES.some(t => 
    t.toLowerCase() === offerType.toLowerCase()
  );
}

/**
 * Appelle l'API QUOTE pour obtenir le prix de l'assurance
 * 
 * @param {Object} bookingData - Données de la réservation
 * @param {string} bookingData.offerId - ID de l'offre (optionnel)
 * @param {string} bookingData.type - Type d'offre (Stage, Séjour, etc.)
 * @param {string} bookingData.formule - "AN" (annulation) ou "MR" (multirisque)
 * @param {string} bookingData.provenance - Code pays ISO 2 lettres (ex: "FR")
 * @param {string} bookingData.destination - Code pays ISO 2 lettres
 * @param {number} bookingData.nombre - Nombre de participants
 * @param {string} bookingData.depart - Date de début (YYYY-MM-DD)
 * @param {string} bookingData.retour - Date de fin (YYYY-MM-DD)
 * @param {number} bookingData.montant_total - Montant total en euros (sans centimes)
 * 
 * @returns {Promise<Object|null>} - Cotation ou null si non éligible/erreur
 */
export async function getInsuranceQuote(bookingData) {
  // Vérifier l'éligibilité du type d'offre
  if (!isInsuranceEligible(bookingData.type)) {
    return null;
  }

  try {
    const payload = {
      offerId: bookingData.offerId || '',
      type: bookingData.type,
      formule: bookingData.formule || 'AN', // Annulation par défaut
      provenance: bookingData.provenance || 'FR',
      destination: bookingData.destination || 'FR',
      nombre: bookingData.nombre,
      depart: bookingData.depart,
      retour: bookingData.retour,
      date_resa: bookingData.date_resa || new Date().toISOString().split('T')[0],
      montant_total: Math.round(bookingData.montant_total)
    };


    const response = await fetch(`${CHAPKA_API_BASE}/quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });


    const data = await response.json();


    if (data.status === 'OK') {
      return {
        premium: data.premium,
        currency: data.currency || 'EUR',
        nom: data.nom || 'Cap Explorer',
        cg_url: data.cg_url,
        cs_url: data.cs_url,
        ipid_url: data.ipid_url
      };
    } else if (data.status === 'DISABLED_FOR_THIS_TYPE') {
      return null;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
}

/**
 * Appelle l'API NOTIFY pour finaliser la souscription après paiement
 * 
 * @param {Object} notifyData - Données de notification
 * @param {string} notifyData.reservationId - ID de la réservation
 * @param {string} notifyData.email - Email du client
 * @param {string} notifyData.formule - "AN" ou "MR"
 * @param {string} notifyData.provenance - Code pays ISO 2 lettres
 * @param {string} notifyData.destination - Code pays ISO 2 lettres
 * @param {number} notifyData.nombre - Nombre de participants
 * @param {string} notifyData.depart - Date de début (YYYY-MM-DD)
 * @param {string} notifyData.retour - Date de fin (YYYY-MM-DD)
 * @param {string} notifyData.prime - Montant de la prime (retourné par QUOTE)
 * @param {Array} notifyData.participants - Liste des participants [{genre, prenom, nom}]
 * 
 * @returns {Promise<Object>} - Résultat de la notification
 */
export async function notifyInsurance(notifyData) {
  try {
    const payload = {
      reservationId: notifyData.reservationId,
      email: notifyData.email,
      formule: notifyData.formule || 'AN',
      provenance: notifyData.provenance || 'FR',
      destination: notifyData.destination || 'FR',
      nombre: notifyData.nombre,
      depart: notifyData.depart,
      retour: notifyData.retour,
      date_resa: notifyData.date_resa || new Date().toISOString().split('T')[0],
      montant_total: notifyData.montant_total,
      prime: notifyData.prime,
      participants: notifyData.participants
    };

    const response = await fetch(`${CHAPKA_API_BASE}/notify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });


    const data = await response.json();


    if (data.status === 'OK') {
      return {
        success: true,
        chapkaId: data.chapka_id
      };
    } else {
      console.error('[Chapka API] ❌ Erreur NOTIFY:', data.message);
      console.error('[Chapka API] 🔍 Debug info NOTIFY:', data.debug || 'Pas de debug disponible');
      return {
        success: false,
        error: data.message
      };
    }
  } catch (error) {
    console.error('[Chapka API] 💥 Erreur appel NOTIFY:', error);
    console.error('[Chapka API] Stack:', error.stack);
    return {
      success: false,
      error: 'Erreur de communication avec le service d\'assurance'
    };
  }
}

/**
 * Convertit une date au format français (DD/MM/YYYY) vers ISO (YYYY-MM-DD)
 * @param {string} frenchDate - Date au format DD/MM/YYYY ou "DD/MM/YYYY HH:MM"
 * @returns {string} - Date au format YYYY-MM-DD
 */
export function convertFrenchDateToISO(frenchDate) {
  if (!frenchDate) return null;
  const datePart = frenchDate.split(' ')[0];
  const [day, month, year] = datePart.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * Extrait les dates de début et fin à partir des critères d'une offre
 * @param {Array} criteria - Tableau des critères de l'offre
 * @returns {Object} - { depart, retour } en format ISO
 */
export function extractDatesFromCriteria(criteria) {
  if (!criteria || criteria.length === 0) {
    return { depart: null, retour: null };
  }

  const dates = criteria.map(c => {
    const datePart = c.date.split(' ')[0];
    const [day, month, year] = datePart.split('/');
    return new Date(year, month - 1, day);
  });

  const sortedDates = dates.sort((a, b) => a - b);
  const firstDate = sortedDates[0];
  const lastDate = sortedDates[sortedDates.length - 1];

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    depart: formatDate(firstDate),
    retour: formatDate(lastDate)
  };
}

export default {
  isInsuranceEligible,
  getInsuranceQuote,
  notifyInsurance,
  convertFrenchDateToISO,
  extractDatesFromCriteria
};

